import React, { type CSSProperties, useState } from 'react'
import { Layout, Menu, Drawer, Button, theme } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { useMenu } from '@refinedev/core'
import { ThemedTitle, useThemedLayoutContext } from '@refinedev/antd'

import { BuildDealerMenuItems } from './buildDealerMenuItems'
import { MenuDrawerButton, useUsesDrawerMenu } from './responsive'

export const DealerSider: React.FC = () => {
  const { token } = theme.useToken()
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } =
    useThemedLayoutContext()
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu()
  const isMobile = useUsesDrawerMenu()

  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys)
  const rootKeys = menuItems.filter((i) => i.children.length > 0).map((i) => i.key)

  const onOpenChange = (keys: string[]): void => {
    const latest = keys.find((k) => !openKeys.includes(k))
    setOpenKeys(latest && rootKeys.includes(latest) ? [latest] : keys)
  }

  const items = BuildDealerMenuItems({ tree: menuItems, selectedKey })

  const menu = (
    <Menu
      selectedKeys={selectedKey ? [selectedKey] : []}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      onClick={({ keyPath }) => {
        if (keyPath.length === 1) setOpenKeys([])
        setMobileSiderOpen(false)
      }}
      mode="inline"
      style={{ paddingTop: '8px', border: 'none', overflow: 'auto', height: 'calc(100% - 72px)' }}
    >
      {items}
    </Menu>
  )

  const titleStyle = (collapsed: boolean): CSSProperties => ({
    width: collapsed ? '80px' : '200px',
    padding: collapsed ? '0' : '0 16px',
    display: 'flex',
    justifyContent: collapsed ? 'center' : 'flex-start',
    alignItems: 'center',
    height: '64px',
    backgroundColor: token.colorBgElevated,
    fontSize: '14px',
  })

  const siderStyle: CSSProperties = {
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid ${token.colorBgElevated}`,
  }

  if (isMobile) {
    return (
      <>
        <Drawer
          data-testid="sider-mobile-drawer"
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          width={200}
          styles={{ body: { padding: 0 } }}
          maskClosable
        >
          <Layout>
            <Layout.Sider style={{ height: '100vh', ...siderStyle }}>
              <div style={titleStyle(false)}>
                <ThemedTitle collapsed={false} />
              </div>
              {menu}
            </Layout.Sider>
          </Layout>
        </Drawer>
        <MenuDrawerButton onClick={() => setMobileSiderOpen(true)} />
      </>
    )
  }

  const CollapseIcon = siderCollapsed ? RightOutlined : LeftOutlined
  return (
    <Layout.Sider
      style={siderStyle}
      collapsible
      collapsed={siderCollapsed}
      onCollapse={(c, type) => {
        if (type === 'clickTrigger') setSiderCollapsed(c)
      }}
      collapsedWidth={80}
      breakpoint="xl"
      trigger={
        <Button
          data-testid="sider-collapse-btn"
          type="text"
          style={{
            borderRadius: 0,
            height: '100%',
            width: '100%',
            backgroundColor: token.colorBgElevated,
          }}
        >
          <CollapseIcon style={{ color: token.colorPrimary }} />
        </Button>
      }
    >
      <div style={titleStyle(siderCollapsed)}>
        <ThemedTitle collapsed={siderCollapsed} />
      </div>
      {menu}
    </Layout.Sider>
  )
}
